<template>
	<div>
		<div class="table_description table_description_filter padding_bottom_s">
			<div class="tertiary_color text_left">
				{{$t("investorSellerDebtorConfig.table.description")}}
			</div>
		</div>
		<div class="table_head table_head_filter">
			<div class="flex flex_between">
				<QuickEnumFilterGroup column-name="status"
									  :enum-def="enumDef.InvestorSellerDebtorConfigStatus"
									  :option-values="[
										  enumDef.InvestorSellerDebtorConfigStatus.New,
										  enumDef.InvestorSellerDebtorConfigStatus.Approved,
										  enumDef.InvestorSellerDebtorConfigStatus.Rejected
									  ]"
									  :default-value="enumDef.InvestorSellerDebtorConfigStatus.New"
									  @apply-filter="applyQuickFilter"
									  :storage-key="`investorSellerDebtorConfig.${tabType}.status`"
				/>
			</div>
		</div>
		<div class="filter_table">
			<AgItem :columnDefs="columnDefs"
					:rowData="rowData"
					v-on:sortChanged="sortChanged"
			></AgItem>
			<Pagination
					:totalNum="totalNum"
					:pageSize="pageSize"
					:sizeChange="sizeChange"
					:currentChange="currentChange"
					:currentPage="page"
			></Pagination>
		</div>
	</div>
</template>

<script>
import {asDecimal, asIndustryText, asLocalizedDate} from "@/components/templates/agGrid/formatter/valueFormatters";
import AgItem from "@/components/templates/agGrid/AgItem.vue";
import Pagination from "@/components/templates/common/Pagination.vue";
import PaginationParent from "@/components/templates/common/PaginationParent.vue";
import {enumDef} from "@/common/enumDefs";
import {riskAssessmentApi} from "@/common/api/riskAssessmentApi";
import {navigationTabs} from "@/common/navigation/navigationTabs";
import QuickEnumFilterGroup from "@/components/newFilter/quickFilters/QuickEnumFilterGroup.vue";
import {customRatingsApi} from "@/common/api/customRatingsApi";

export default {
		extends: PaginationParent,
		components: {
			QuickEnumFilterGroup,
			AgItem,
			Pagination
		},
		props: {
			tabType: {type: String, default: ""},
		},
		data() {
			return {
        		ratingConfig: null,
        		ratingConfigLoaded: false,
				columnDefs: [],
				filters: [],
				rowData: null,
				sorting: [],
				enumDef: enumDef
			};
		},
		computed: {
			firstTabHeaderGroupName() {
				return this.isTabTypeSeller ? this.$t("common.seller") : this.$t("common.debtor");
			},
			amlCheckToopTip() {
				return this.isTabTypeSeller? this.$t("common.amlChecks.tooltip.seller"): this.$t("common.amlChecks.tooltip.debtor");
			},
			isTabTypeSeller() {
				return this.tabType === navigationTabs.investor.riskAssessment.seller;
			}
		},
		beforeMount() {
			if (!this.ratingConfigLoaded) {
				// Column definitions are set based on the config
				this.loadRatingConfig();
			} else {
				// Column definitions can be set right away
				this.setColumnDefs();
			}

			// Initial list is loaded when quick filters are initialized
		},
		methods: {
			setColumnDefs() {
				this.columnDefs = [
					{
						headerName: this.firstTabHeaderGroupName,
						children: [
							{
								headerName: this.$t("common.status"),
								minWidth: 120,
								maxWidth: 120,
								field: "status",
								cellRendererFramework: "EnumAsText",
								enumDef: enumDef.InvestorSellerDebtorConfigStatus,
								sortable: true,
							},
							{
								headerName: this.$t("common.name"),
								minWidth: 150,
								field: "companyName",
								nameType: "companyName",
								idType: "companyId",
								cellRendererFramework: "CompanyTem",
								sortable: true
							},
							{
								hide: !this.ratingConfig || !this.ratingConfig.showRating1,
								headerName: this.ratingConfig && this.ratingConfig.nameRating1
									? this.ratingConfig.nameRating1
									: this.$t("investor.common.table.header.customRating1"),
								field: "customRating1",
								valueFormatter: asDecimal(),
								minWidth: 120,
								maxWidth: 140,
								sortable: true
							},
							{
								hide: !this.ratingConfig || !this.ratingConfig.showRating2,
								headerName: this.ratingConfig && this.ratingConfig.nameRating2
									? this.ratingConfig.nameRating2
									: this.$t("investor.common.table.header.customRating2"),
								field: "customRating2",
								valueFormatter: asDecimal(),
								minWidth: 120,
								maxWidth: 140,
								sortable: true
							},
							{
								hide: !this.ratingConfig || !this.ratingConfig.showRating3,
								headerName: this.ratingConfig && this.ratingConfig.nameRating3
									? this.ratingConfig.nameRating3
									: this.$t("investor.common.table.header.customRating3"),
								field: "customRating3",
								valueFormatter: asDecimal(),
								minWidth: 120,
								maxWidth: 140,
								sortable: true
							},
							...this.isTabTypeSeller ? [
								{
									headerName: this.$t("common.partner"),
									minWidth: 120,
									field: "partnerName",
									cellRendererFramework: "GeneralShortenedDisplay",
									sortable: true
								}
							] : [],
							{
								headerName: this.$t("common.industry"),
								field: "companyIndustryNaceCode",
								minWidth: 200,
								valueFormatter: asIndustryText({ detailLevel: enumDef.industryDetailLevel.Section }),
								cellRendererFramework: "GeneralShortenedDisplay",
								sortable: true
							},
							{
								headerName: this.$t("common.country"),
								minWidth: 90,
								maxWidth: 90,
								field: "country",
								sortable: true,
							},
							{
								headerName: this.$t("common.city"),
								minWidth: 120,
								maxWidth: 200,
								field: "city",
								cellRendererFramework: "GeneralShortenedDisplay",
								sortable: true,
							},
							{
								headerName: this.$t("common.postcode"),
								minWidth: 100,
								maxWidth: 120,
								field: "postCode",
								sortable: true,
							},
							{
								headerName: this.$t("common.street"),
								minWidth: 200,
								field: "street",
								cellRendererFramework: "GeneralShortenedDisplay",
								sortable: true,
							},
							{
								headerName: this.$t("common.vatId"),
								minWidth: 120,
								field: "companyTaxNumber",
								sortable: true
							},
							...this.isTabTypeSeller ? [
								{
									headerName: this.$t("common.incorporationTime"),
									field: "companyIncorporationTime",
									valueFormatter: asLocalizedDate(),
									minWidth: 170,
									maxWidth: 220,
									sortable: true
								}
							] : [],
							{
								headerName: this.$t("common.amlChecks"),
								headerComponentParams: {
									tooltip: this.amlCheckToopTip,
									showTooltipIcon: true
								},
								minWidth: 125,
								maxWidth: 125,
								field: "companyAmlCheckStatus",
								cellRendererFramework: "EnumAsIcon",
								enumDef: enumDef.AmlCheckStatus,
								sortable: true
							},
						]
					},
					{
						headerName: this.$t("common.evaluation"),
						headerClass: "bg_special",
						children: [
							{
								headerName: this.$t("common.actions"),
								cellRendererFramework: "InvestorSellerDebtorConfigTableOperation",
								cellRendererParams: {
									approve: this.confirmApprove,
									reject:this.confirmReject
								},
								minWidth: 200,
								maxWidth: 200,
								pinned: "right",
							},
						]
					}
				];
			},
			sortChanged(sortData) {
				this.sorting = sortData;
				this.initPage();
				this.getList();
			},
			getList() {
				let postData = {
					columns: [],
					filters: this.filters || [],
					maxResults: this.maxResults,
					offset: this.offset,
					sorting: this.sorting
				};

				if (this.tabType === navigationTabs.investor.riskAssessment.debtor) {
					riskAssessmentApi.inspection.listDebtors(postData)
					.then(res => {
						this.setTotalNum(res.recordsTotal);
						this.rowData = res.data;
					})
					.catch(this.toast);
				} else if (this.tabType === navigationTabs.investor.riskAssessment.seller) {
					riskAssessmentApi.inspection.listSellers(postData)
					.then(res => {
						this.setTotalNum(res.recordsTotal);
						this.rowData = res.data;
					})
					.catch(this.toast);
				}
			},
			confirmApprove(rowNode) {
				let option = {
					msg:this.$t("investorSellerDebtorConfig.approve.confirm.msg", {companyName: rowNode.data.companyName}),
					confirmFun: this.approveConfig,
					params: rowNode
				}
				this.confirmMeg(option, true)
			},
			confirmReject(rowNode) {
				let option = {
					msg:this.$t("investorSellerDebtorConfig.reject.confirm.msg", {companyName: rowNode.data.companyName}),
					confirmFun: this.rejectConfig,
					params: rowNode
				}
				this.confirmMeg(option, true)
			},
			approveConfig(rowNode) {
				riskAssessmentApi.operation.approveInvestorConfig(rowNode.data.id)
				.then(() => {
					this.toastSuccess();
					this.updateRowDataAfterStatusChanged(rowNode, enumDef.InvestorSellerDebtorConfigStatus.Approved);
				})
				.catch(this.toast);
			},
			rejectConfig(rowNode) {
				riskAssessmentApi.operation.rejectInvestorConfig(rowNode.data.id)
				.then(() => {
					this.toastSuccess();
					this.updateRowDataAfterStatusChanged(rowNode, enumDef.InvestorSellerDebtorConfigStatus.Rejected);
				})
				.catch(this.toast);
			},
			applyQuickFilter(filters) {
				this.filters = filters;
			},
			loadRatingConfig() {
				return new Promise((resolve => {
					customRatingsApi.inspection.getConfig()
							.then(res => {
								this.ratingConfig = res;
								resolve();
							})
							.catch(() => {
								// We ignore errors for the config and show the configs as if no config exists
								resolve();
							}).finally(() => {
								this.ratingConfigLoaded = true;
							});
				}));
			},
			updateRowDataAfterStatusChanged(rowNode, newStatus) {
				// Instead of reloading the whole list like normally, we simply update the row itself.
				// This avoids that the entry "jumps" to a different position because of the new status
				rowNode.setDataValue("status", newStatus);
			}
		},
		watch: {
			filters() {
				this.getList();
			},
			ratingConfigLoaded() {
				this.setColumnDefs();
			}
		},
	};
</script>